import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const BaseVegetableCharacter = ({
                                    position,
                                    rotation,
                                    isWalking = false,
                                    lookPitch = 0,
                                    scale = 1,
                                    renderBody,
                                    primaryColor = "#8bc34a", // Default green color
                                    secondaryColor = "#689f38" // Darker green for secondary elements
                                }) => {
    const groupRef = useRef();
    const bodyRef = useRef();
    const leftLegRef = useRef();
    const rightLegRef = useRef();
    const eyesGroupRef = useRef();

    // Animation and positioning logic
    useFrame((state) => {
        if (!groupRef.current) return;

        // Calculate jump height (anything above standard ground position)
        const jumpHeight = Math.max(0, position[1] - 1.0);

        // Position based on scale to keep feet on ground + jump height
        const scaledHeightFromOriginToFeet = scale * 1.0;

        // Apply position
        groupRef.current.position.set(
            position[0],
            scaledHeightFromOriginToFeet + jumpHeight,
            position[2]
        );
        groupRef.current.rotation.set(0, rotation[1], 0);

        // Apply scale
        groupRef.current.scale.set(scale, scale, scale);

        // Apply eye rotation (pitch)
        if (eyesGroupRef.current) {
            eyesGroupRef.current.rotation.x = lookPitch;
        }

        // Apply walking animation
        if (isWalking && leftLegRef.current && rightLegRef.current) {
            const walkSpeed = 8;
            const legRotationMax = 0.4;

            // Alternate legs for walking
            leftLegRef.current.rotation.x = Math.sin(state.clock.elapsedTime * walkSpeed) * legRotationMax;
            rightLegRef.current.rotation.x = Math.sin(state.clock.elapsedTime * walkSpeed + Math.PI) * legRotationMax;

            // Add subtle body bobbing
            if (bodyRef.current) {
                bodyRef.current.position.y = Math.abs(Math.sin(state.clock.elapsedTime * walkSpeed)) * 0.05 + 0.7;
            }
        } else {
            // Reset legs when not walking
            if (leftLegRef.current && rightLegRef.current) {
                leftLegRef.current.rotation.x = 0;
                rightLegRef.current.rotation.x = 0;
            }

            // Gentle idle bobbing
            if (bodyRef.current) {
                bodyRef.current.position.y = Math.sin(state.clock.elapsedTime * 1.5) * 0.05 + 0.7;
            }
        }
    });

    return (
        <group ref={groupRef}>
            {/* Body - this will be different for each vegetable */}
            <group ref={bodyRef} position={[0, 0.7, 0]}>
                {/* Render the specific vegetable body */}
                {renderBody && renderBody(bodyRef)}

                {/* Eyes Group - common to all characters */}
                <group ref={eyesGroupRef} position={[0, 0.1, 0.2]}>
                    {/* Left Eye */}
                    <group position={[-0.08, 0, 0]}>
                        <mesh castShadow>
                            <sphereGeometry args={[0.05, 8, 8]} />
                            <meshStandardMaterial color="white" />
                        </mesh>
                        <mesh position={[0, 0, 0.04]}>
                            <sphereGeometry args={[0.02, 8, 8]} />
                            <meshStandardMaterial color="black" />
                        </mesh>
                    </group>

                    {/* Right Eye */}
                    <group position={[0.08, 0, 0]}>
                        <mesh castShadow>
                            <sphereGeometry args={[0.05, 8, 8]} />
                            <meshStandardMaterial color="white" />
                        </mesh>
                        <mesh position={[0, 0, 0.04]}>
                            <sphereGeometry args={[0.02, 8, 8]} />
                            <meshStandardMaterial color="black" />
                        </mesh>
                    </group>
                </group>

                {/* Arms - common arm structure but using primaryColor */}
                <group position={[0, 0, 0]}>
                    {/* Left Arm */}
                    <mesh position={[-0.25, -0.05, 0]} rotation={[0, 0, Math.PI / 3]} castShadow>
                        <cylinderGeometry args={[0.03, 0.03, 0.3, 8]} />
                        <meshStandardMaterial color={secondaryColor} />
                    </mesh>

                    {/* Right Arm */}
                    <mesh position={[0.25, -0.05, 0]} rotation={[0, 0, -Math.PI / 3]} castShadow>
                        <cylinderGeometry args={[0.03, 0.03, 0.3, 8]} />
                        <meshStandardMaterial color={secondaryColor} />
                    </mesh>
                </group>
            </group>

            {/* Legs - common to all characters */}
            <group position={[0, 0, 0]}>
                {/* Left Leg */}
                <group ref={leftLegRef} position={[-0.12, 0, 0]}>
                    <mesh position={[0, -0.5, 0]} castShadow>
                        <cylinderGeometry args={[0.04, 0.04, 1.0, 8]} />
                        <meshStandardMaterial color={secondaryColor} />
                    </mesh>
                    {/* Foot */}
                    <mesh position={[0, -1.0, 0.05]} castShadow>
                        <boxGeometry args={[0.08, 0.05, 0.15]} />
                        <meshStandardMaterial color={secondaryColor} />
                    </mesh>
                </group>

                {/* Right Leg */}
                <group ref={rightLegRef} position={[0.12, 0, 0]}>
                    <mesh position={[0, -0.5, 0]} castShadow>
                        <cylinderGeometry args={[0.04, 0.04, 1.0, 8]} />
                        <meshStandardMaterial color={secondaryColor} />
                    </mesh>
                    {/* Foot */}
                    <mesh position={[0, -1.0, 0.05]} castShadow>
                        <boxGeometry args={[0.08, 0.05, 0.15]} />
                        <meshStandardMaterial color={secondaryColor} />
                    </mesh>
                </group>
            </group>
        </group>
    );
};

export default BaseVegetableCharacter;
